import React, { FC, useMemo, useCallback } from "react";
import DocumentTitle from "react-document-title";
import { useLocation, useHistory } from "react-router-dom";
import { subDays } from "date-fns";
import { useGetOrgDashboardQuery } from "generated/graphql";
import { useFormCompletion } from "components/FormCompletion";
import { LeadsDashboardView, DashboardFilterValue, DashboardFilterType, DashboardActiveFilters } from "components/LeadsDashboard";
import { OrganizationSecondaryNav } from "components/OrgSecondaryNav";
import { Button, LoadingSpinner, PageAlert } from "@xapp/components";
import {
    FILTER_PARAM_MAP,
    LEADS_FILTER_MAP,
    validateFilterValue,
    isValidOrganizationId,
    parseBooleanFilter
} from "constants/dashboardFilters";
import styles from "./styles/OrgDashboard.module.scss";

/** Default date range in days */
const DEFAULT_DAYS_BACK = 30;

export interface OrgDashboardContainerProps {
    organizationId: string;
    /**
     * When embedded in another page (like Overview), hides the title header and nav
     */
    embedded?: boolean;
    /**
     * Start date for date range filtering (used when embedded)
     */
    startDate?: Date;
    /**
     * End date for date range filtering (used when embedded)
     */
    endDate?: Date;
}

export const OrgDashboardContainer: FC<OrgDashboardContainerProps> = ({
    organizationId,
    embedded = false,
    startDate: propStartDate,
    endDate: propEndDate
}) => {
    const location = useLocation();
    const history = useHistory();

    // Parse URL search params
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    // Get dates from URL query params (only when not embedded)
    const urlStartDate = !embedded ? searchParams.get('startDate') : null;
    const urlEndDate = !embedded ? searchParams.get('endDate') : null;

    // Calculate default dates (Last 30 Days)
    const defaultDates = useMemo(() => {
        const now = new Date();
        return {
            start: subDays(now, DEFAULT_DAYS_BACK).toISOString(),
            end: now.toISOString()
        };
    }, []);

    // When embedded, use props; otherwise use URL params with Last 30 Days as default
    const startDate = embedded && propStartDate
        ? propStartDate.toISOString()
        : urlStartDate || defaultDates.start;
    const endDate = embedded && propEndDate
        ? propEndDate.toISOString()
        : urlEndDate || defaultDates.end;

    // Get filter params from URL with validation (only when not embedded)
    const filters = useMemo(() => {
        if (embedded) return {};
        return {
            source: validateFilterValue(searchParams.get('source')),
            referringSource: validateFilterValue(searchParams.get('referringSource')),
            contactType: validateFilterValue(searchParams.get('contactType')),
            serviceType: validateFilterValue(searchParams.get('serviceType')),
            jobType: validateFilterValue(searchParams.get('jobType')),
            urgency: validateFilterValue(searchParams.get('urgency')),
            highValueLead: parseBooleanFilter(searchParams.get('highValueLead')),
            zipCode: validateFilterValue(searchParams.get('zipCode')),
            // Lead taxonomy filters — set by clicking a slice on one of the taxonomy pies
            contactMethod: validateFilterValue(searchParams.get('contactMethod')),
            agentType: validateFilterValue(searchParams.get('agentType')),
            capturePlatform: validateFilterValue(searchParams.get('capturePlatform')),
            sourceSystem: validateFilterValue(searchParams.get('sourceSystem'))
        };
    }, [embedded, searchParams]);

    const isValidId = useMemo(() => isValidOrganizationId(organizationId), [organizationId]);

    const [{ data, fetching, error }, reexecuteQuery] = useGetOrgDashboardQuery({
        requestPolicy: "cache-and-network",
        variables: {
            organizationId,
            startDate,
            endDate,
            source: filters.source,
            referringSource: filters.referringSource,
            urgency: filters.urgency,
            highValueLead: filters.highValueLead,
            contactType: filters.contactType,
            serviceType: filters.serviceType,
            jobType: filters.jobType,
            contactMethod: filters.contactMethod,
            agentType: filters.agentType,
            capturePlatform: filters.capturePlatform,
            sourceSystem: filters.sourceSystem,
            zipCode: filters.zipCode
        },
        pause: !isValidId
    });

    const formCompletion = useFormCompletion({ organizationId, start: startDate, end: endDate, pause: !isValidId });

    const handleRetry = useCallback(() => {
        reexecuteQuery({ requestPolicy: "network-only" });
    }, [reexecuteQuery]);

    const handleDateRangeChange = useCallback((start: string | undefined, end: string | undefined) => {
        if (embedded) return; // Don't update URL when embedded

        const newParams = new URLSearchParams(location.search);
        if (start) {
            newParams.set('startDate', start);
        } else {
            newParams.delete('startDate');
        }
        if (end) {
            newParams.set('endDate', end);
        } else {
            newParams.delete('endDate');
        }
        const newSearch = newParams.toString();
        history.replace({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : ''
        });
    }, [embedded, location.search, location.pathname, history]);

    const handleFilterApply = useCallback((filter: DashboardFilterValue) => {
        if (embedded) return; // Don't update URL when embedded

        const paramName = FILTER_PARAM_MAP[filter.filterType];
        if (!paramName) return;

        const newParams = new URLSearchParams(location.search);
        newParams.set(paramName, filter.value);
        const newSearch = newParams.toString();
        history.push({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : ''
        });
    }, [embedded, location.search, location.pathname, history]);

    const handleViewLeads = useCallback(() => {
        if (embedded) return;

        // Navigate to org-level leads list with all active filters applied
        const leadsPath = `/${organizationId}/leads`;
        const newParams = new URLSearchParams();

        // Add all active filters
        (Object.keys(filters) as Array<keyof DashboardActiveFilters>).forEach(filterType => {
            const value = filters[filterType];
            if (value !== undefined) {
                const leadsParamName = LEADS_FILTER_MAP[filterType];
                if (leadsParamName) {
                    newParams.set(leadsParamName, value.toString());
                }
            }
        });

        // Preserve date range
        if (startDate) newParams.set('startDate', startDate);
        if (endDate) newParams.set('endDate', endDate);

        const newSearch = newParams.toString();
        history.push({
            pathname: leadsPath,
            search: newSearch ? `?${newSearch}` : ''
        });
    }, [embedded, organizationId, filters, startDate, endDate, history]);

    const handleFilterClear = useCallback((filterType: DashboardFilterType) => {
        if (embedded) return;

        const paramName = FILTER_PARAM_MAP[filterType];
        if (!paramName) return;

        const newParams = new URLSearchParams(location.search);
        newParams.delete(paramName);
        const newSearch = newParams.toString();
        history.push({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : ''
        });
    }, [embedded, location.search, location.pathname, history]);

    const handleClearAllFilters = useCallback(() => {
        if (embedded) return;

        const newParams = new URLSearchParams(location.search);
        // Keep date params, clear all filter params
        Object.values(FILTER_PARAM_MAP).forEach(param => {
            newParams.delete(param);
        });
        const newSearch = newParams.toString();
        history.push({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : ''
        });
    }, [embedded, location.search, location.pathname, history]);

    if (!isValidId) {
        return (
            <PageAlert background="red">
                <div>
                    <h3>Invalid Organization</h3>
                    <p>The organization ID provided is invalid.</p>
                </div>
            </PageAlert>
        );
    }

    if (error) {
        // Log error for debugging
        console.error('[OrgDashboard] Error loading dashboard:', error);

        // Determine user-friendly error message based on error type
        const isNetworkError = error.networkError !== undefined;
        const errorTitle = isNetworkError
            ? "Connection Error"
            : "Failed to load dashboard data";
        const errorDescription = isNetworkError
            ? "Unable to connect to the server. Please check your internet connection and try again."
            : error.message || "An unexpected error occurred while loading the dashboard.";

        return (
            <PageAlert background="red">
                <div className={styles.errorContainer}>
                    <h3>{errorTitle}</h3>
                    <p>{errorDescription}</p>
                    <Button
                        variation="secondary"
                        label="Try Again"
                        onClick={handleRetry}
                    />
                </div>
            </PageAlert>
        );
    }

    if (fetching && !data) {
        return <LoadingSpinner />;
    }

    const leadsData = data?.dashboard?.leads;

    const dashboardView = (
        <LeadsDashboardView
            totalLeads={leadsData?.totalLeads}
            highValueLeadsPercentage={leadsData?.highValueLeadsPercentage}
            leadsBySource={leadsData?.leadsBySource}
            leadsByReferringSource={leadsData?.leadsByReferringSource}
            leadsByContactType={leadsData?.leadsByContactType}
            leadsByServiceType={leadsData?.leadsByServiceType}
            leadsByJobType={leadsData?.leadsByJobType}
            leadsByUrgency={leadsData?.leadsByUrgency}
            leadsByContactMethod={leadsData?.leadsByContactMethod}
            leadsByAgentType={leadsData?.leadsByAgentType}
            leadsByCapturePlatform={leadsData?.leadsByCapturePlatform}
            leadsBySourceSystem={leadsData?.leadsBySourceSystem}
            leadsByCaptureSurface={leadsData?.leadsByCaptureSurface}
            leadsByUtmSource={leadsData?.leadsByUtmSource}
            leadsByUtmMedium={leadsData?.leadsByUtmMedium}
            leadsByUtmCampaign={leadsData?.leadsByUtmCampaign}
            leadsByDayOfWeek={leadsData?.leadsByDayOfWeek}
            outsideBusinessHoursPercentage={leadsData?.outsideBusinessHoursPercentage}
            businessHoursSource={leadsData?.businessHoursSource}
            businessHoursSummary={leadsData?.businessHoursSummary}
            topTerms={leadsData?.topTerms}
            leadsByApp={leadsData?.leadsByApp}
            topEngagementsByZip={leadsData?.topEngagementsByZip}
            organizationId={organizationId}
            loading={fetching}
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={handleDateRangeChange}
            embedded={embedded}
            onFilterApply={!embedded ? handleFilterApply : undefined}
            onViewLeads={!embedded ? handleViewLeads : undefined}
            activeFilters={!embedded ? filters : undefined}
            formCompletion={formCompletion}
            onFilterClear={!embedded ? handleFilterClear : undefined}
            onClearAllFilters={!embedded ? handleClearAllFilters : undefined}
        />
    );

    // When embedded, just return the dashboard view without chrome
    if (embedded) {
        return dashboardView;
    }

    // Full org-level dashboard with document title and nav
    return (
        <DocumentTitle title={`XAPP | ${organizationId} - Leads Dashboard`}>
            <>
                {dashboardView}
                <OrganizationSecondaryNav organizationId={organizationId} />
            </>
        </DocumentTitle>
    );
};
